/**
 * Universal UI string translator.
 *
 * Wraps any English string and renders the translated version when the
 * current locale ≠ EN. Three-tier cache:
 *
 *   1. In-memory `Map`          → instant, dies on page reload
 *   2. `localStorage` (per-lang) → instant, persists across reloads
 *   3. Server-side Mongo cache   → shared across all visitors
 *
 * Requests for the same string are de-duplicated; in-flight requests get
 * batched (50ms debounce) into a single `POST /api/translate/batch` call.
 *
 * Usage:
 *     import { TX } from "@/components/TX";
 *     <TX>Cookie Settings</TX>
 *
 *     // Hook form for prop strings:
 *     const t = useTX();
 *     <input placeholder={t("Search the collection")} />
 *
 * Notes:
 *   • Strings must be plain English literals. Don't pass interpolated text
 *     containing user data — the cache key is the literal.
 *   • For interpolated/dynamic strings, translate the template once and
 *     interpolate manually.
 */
import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import api from "../lib/api";
import { useLang } from "../context/LanguageContext";

const LS_PREFIX = "opik_tx_v1::";  // localStorage key prefix
const MEMORY_CACHE = new Map();    // `${lang}::${text}` -> translated
const PENDING = new Map();         // `${lang}::${text}` -> Promise<string>

let _flushTimer = null;
const _queue = new Map(); // `${lang}::${text}` -> { text, lang, resolvers: [] }

const _loadFromLocalStorage = (lang) => {
  try {
    const raw = typeof window !== "undefined" ? window.localStorage.getItem(LS_PREFIX + lang) : null;
    return raw ? JSON.parse(raw) : {};
  } catch (e) { return {}; }
};
const _persistToLocalStorage = (lang, dict) => {
  try { window.localStorage.setItem(LS_PREFIX + lang, JSON.stringify(dict)); } catch (e) { /* quota etc. */ }
};
// Hydrate memory cache from localStorage on first load per locale
const _hydrated = new Set();
const _hydrate = (lang) => {
  if (_hydrated.has(lang)) return;
  _hydrated.add(lang);
  const dict = _loadFromLocalStorage(lang);
  Object.entries(dict).forEach(([text, translated]) => {
    MEMORY_CACHE.set(`${lang}::${text}`, translated);
  });
};

/**
 * Schedule a flush of the queued translation requests in 50ms. Multiple
 * `<TX>` mounts in the same render tick coalesce into one network call.
 * Batches are chunked to MAX_BATCH so a 60-string page doesn't block on
 * a single long-running request.
 */
const MAX_BATCH = 15;

const _flush = async () => {
  if (_queue.size === 0) return;
  // Group by target language
  const byLang = new Map();
  for (const item of _queue.values()) {
    if (!byLang.has(item.lang)) byLang.set(item.lang, []);
    byLang.get(item.lang).push(item);
  }
  _queue.clear();

  await Promise.all([...byLang.entries()].map(async ([lang, items]) => {
    // Process in chunks so each request returns quickly (~3-6s typical)
    const dict = _loadFromLocalStorage(lang);
    const chunks = [];
    for (let i = 0; i < items.length; i += MAX_BATCH) {
      chunks.push(items.slice(i, i + MAX_BATCH));
    }
    await Promise.all(chunks.map(async (chunk) => {
      const texts = chunk.map((i) => i.text);
      try {
        const { data } = await api.post("/translate/batch", { texts, target_lang: lang });
        const translations = data?.translations || [];
        chunk.forEach((it, idx) => {
          const t = translations[idx] || it.text;
          MEMORY_CACHE.set(`${lang}::${it.text}`, t);
          dict[it.text] = t;
          it.resolvers.forEach((r) => r(t));
          PENDING.delete(`${lang}::${it.text}`);
        });
      } catch (e) {
        chunk.forEach((it) => {
          MEMORY_CACHE.set(`${lang}::${it.text}`, it.text);
          it.resolvers.forEach((r) => r(it.text));
          PENDING.delete(`${lang}::${it.text}`);
        });
      }
    }));
    _persistToLocalStorage(lang, dict);
  }));
};

const _scheduleFlush = () => {
  if (_flushTimer) return;
  _flushTimer = setTimeout(() => {
    _flushTimer = null;
    _flush();
  }, 50);
};

/**
 * Translate one string to `lang`. Returns a Promise resolving with the
 * translated text (or the original on failure). Uses all 3 cache tiers.
 */
export const translateText = (text, lang) => {
  if (!text || lang === "en" || lang === "EN") return Promise.resolve(text);
  _hydrate(lang);
  const key = `${lang}::${text}`;
  if (MEMORY_CACHE.has(key)) return Promise.resolve(MEMORY_CACHE.get(key));
  if (PENDING.has(key)) return PENDING.get(key);
  const promise = new Promise((resolve) => {
    const existing = _queue.get(key);
    if (existing) {
      existing.resolvers.push(resolve);
    } else {
      _queue.set(key, { text, lang, resolvers: [resolve] });
    }
    _scheduleFlush();
  });
  PENDING.set(key, promise);
  return promise;
};

/**
 * `<TX>some english</TX>` — renders the translated string lazily.
 * While the translation is loading the original English is shown,
 * which keeps the initial paint flicker-free.
 */
export const TX = ({ children }) => {
  const { apiLang } = useLang();
  const text = typeof children === "string" ? children : (Array.isArray(children) ? children.join("") : "");
  const [out, setOut] = useState(() => {
    if (!text || apiLang === "en") return text;
    _hydrate(apiLang);
    const cached = MEMORY_CACHE.get(`${apiLang}::${text}`);
    return cached ?? text;
  });
  useEffect(() => {
    let cancelled = false;
    if (!text || apiLang === "en") { setOut(text); return; }
    translateText(text, apiLang).then((v) => { if (!cancelled) setOut(v); });
    return () => { cancelled = true; };
  }, [text, apiLang]);
  return out;
};

/**
 * Hook variant — returns a `(text) => translated` function. Each call
 * re-renders the host component once its translation resolves.
 *
 * Usage:
 *   const t = useTX();
 *   <input placeholder={t("Search the collection")} />
 */
export const useTX = () => {
  const { apiLang } = useLang();
  const [, force] = useState(0);
  const subscribed = useRef(new Set());

  const t = useCallback((text) => {
    if (!text || apiLang === "en") return text;
    _hydrate(apiLang);
    const key = `${apiLang}::${text}`;
    const cached = MEMORY_CACHE.get(key);
    if (cached !== undefined) return cached;
    if (!subscribed.current.has(key)) {
      subscribed.current.add(key);
      translateText(text, apiLang).then(() => force((n) => n + 1));
    }
    return text;
  }, [apiLang]);

  return useMemo(() => t, [t]);
};

export default TX;
